export interface SquarifyInput {
  id: string;
  value: number;
}

export interface SquarifyRect {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export function squarify(
  data: SquarifyInput[],
  width: number,
  height: number
): SquarifyRect[] {
  if (data.length === 0 || width <= 0 || height <= 0) return [];

  const totalValue = data.reduce((sum, d) => sum + d.value, 0);
  if (totalValue <= 0) return [];

  // Normalize values to fill the total area
  const totalArea = width * height;
  const items = data
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((d) => ({ id: d.id, area: (d.value / totalValue) * totalArea }));

  const rects: SquarifyRect[] = [];
  let x = 0;
  let y = 0;
  let w = width;
  let h = height;

  function layoutRow(row: { id: string; area: number }[], rowArea: number, side: number, vertical: boolean) {
    const otherSide = rowArea / side;
    let offset = 0;
    for (const item of row) {
      const itemSide = item.area / otherSide;
      if (vertical) {
        rects.push({ id: item.id, x, y: y + offset, width: otherSide, height: itemSide });
      } else {
        rects.push({ id: item.id, x: x + offset, y, width: itemSide, height: otherSide });
      }
      offset += itemSide;
    }
    if (vertical) {
      x += otherSide;
      w -= otherSide;
    } else {
      y += otherSide;
      h -= otherSide;
    }
  }

  function worstRatio(row: { id: string; area: number }[], side: number): number {
    const rowArea = row.reduce((s, r) => s + r.area, 0);
    const otherSide = rowArea / side;
    let worst = 0;
    for (const item of row) {
      const itemSide = item.area / otherSide;
      const ratio = Math.max(otherSide / itemSide, itemSide / otherSide);
      if (ratio > worst) worst = ratio;
    }
    return worst;
  }

  let i = 0;
  while (i < items.length) {
    const vertical = w >= h;
    const side = vertical ? h : w;
    const row: { id: string; area: number }[] = [items[i]];
    i++;

    while (i < items.length) {
      const newRow = [...row, items[i]];
      if (worstRatio(newRow, side) <= worstRatio(row, side)) {
        row.push(items[i]);
        i++;
      } else {
        break;
      }
    }

    const rowArea = row.reduce((s, r) => s + r.area, 0);
    layoutRow(row, rowArea, side, vertical);
  }

  return rects;
}
